/* ⌘K command palette.
   Mounted once from Dashboard. Visible when `useApp.paletteOpen` is true.
   Supports:
     - fuzzy-substring search over command label + keywords
     - ↑/↓ keyboard navigation + Enter to run
     - ESC / click-outside to close
     - scrollable list (max ~8 rows visible, content scrolls if longer) */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { filterCommands, type Command } from "./commands"
import { useApp } from "./store"

interface CommandPaletteProps {
  commands: Command[]
}

export function CommandPalette({ commands }: CommandPaletteProps) {
  const open = useApp((s) => s.paletteOpen)
  const setOpen = useApp((s) => s.setPaletteOpen)

  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  // Derive the visible list from the live command registry + user query.
  const filtered = useMemo(() => filterCommands(commands, query), [commands, query])

  // Keep `selected` in a valid range whenever the filtered list changes.
  useEffect(() => {
    setSelected((prev) => {
      if (filtered.length === 0) return 0
      if (prev >= filtered.length) return filtered.length - 1
      if (prev < 0) return 0
      return prev
    })
  }, [filtered])

  // Reset transient state whenever the palette opens — always start blank with
  // the first row selected.
  useEffect(() => {
    if (open) {
      setQuery("")
      setSelected(0)
      // Next tick focus — the <input autoFocus> covers first mount, but
      // re-opens without unmount need an explicit nudge.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  const close = useCallback(() => setOpen(false), [setOpen])

  const runAt = useCallback(
    (idx: number) => {
      const cmd = filtered[idx]
      if (!cmd) return
      close()
      // Defer execution one tick so the palette unmounts before the command
      // fires — avoids the about/alert() being nested under the overlay and
      // avoids focus-fight with newly-opened modals (e.g. region deep-dive).
      setTimeout(() => cmd.run(), 0)
    },
    [filtered, close],
  )

  // Keep the selected row scrolled into view while arrowing through a long list.
  useEffect(() => {
    if (!open) return
    const container = listRef.current
    if (!container) return
    const row = container.querySelector<HTMLElement>(`[data-cmd-index="${selected}"]`)
    if (row) row.scrollIntoView({ block: "nearest" })
  }, [selected, open])

  // Document-level ESC fallback: if focus somehow escapes the card
  // (mouse click lands on a non-focusable region), ESC should still close.
  useEffect(() => {
    if (!open) return
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault()
        close()
      }
    }
    document.addEventListener("keydown", onDocKey)
    return () => document.removeEventListener("keydown", onDocKey)
  }, [open, close])

  if (!open) return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelected((prev) => (filtered.length === 0 ? 0 : (prev + 1) % filtered.length))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelected((prev) =>
        filtered.length === 0 ? 0 : (prev - 1 + filtered.length) % filtered.length,
      )
    } else if (e.key === "Enter") {
      e.preventDefault()
      runAt(selected)
    } else if (e.key === "Escape") {
      e.preventDefault()
      close()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onMouseDown={(e) => {
        // Any mousedown on the backdrop closes; inner card stops propagation.
        if (e.target === e.currentTarget) close()
      }}
    >
      <div className="w-full flex justify-center" style={{ paddingTop: "12vh" }}>
        <div
          className="ui-panel ui-panel--glass w-[92%] max-w-[600px] rounded shadow-lg overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
        >
          {/* Search row */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[color:var(--border)]">
            <span
              aria-hidden="true"
              className="text-[color:var(--text-dim)] text-sm leading-none"
            >
              ⌘
            </span>
            <input
              ref={inputRef}
              autoFocus
              type="text"
              className="search-input flex-1"
              placeholder="type a command…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setSelected(0)
              }}
              aria-label="Command search"
              spellCheck={false}
              autoComplete="off"
            />
            <kbd>ESC</kbd>
          </div>

          {/* Results list — ~8 rows visible, scrolls if more.
              40px per row × 8 = 320px; header eats the rest. */}
          <div
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: "320px" }}
            role="listbox"
            aria-label="Commands"
          >
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-[11px] text-[color:var(--text-dim)] uppercase tracking-widest">
                no matching commands
              </div>
            ) : (
              filtered.map((cmd, idx) => {
                const isSelected = idx === selected
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-cmd-index={idx}
                    onMouseEnter={() => setSelected(idx)}
                    onClick={() => runAt(idx)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 border-b border-[color:var(--border)] last:border-b-0 transition-colors"
                    style={
                      isSelected
                        ? {
                            background: "rgba(34, 211, 238, 0.1)",
                            color: "var(--text)",
                          }
                        : { color: "var(--text)" }
                    }
                  >
                    <span
                      aria-hidden="true"
                      className="text-base w-5 shrink-0 text-center leading-none"
                      style={{ color: isSelected ? "var(--accent)" : "var(--text-muted)" }}
                    >
                      {cmd.icon ?? "›"}
                    </span>
                    <span className="flex-1 truncate text-[13px]">{cmd.label}</span>
                    {cmd.shortcut && (
                      <kbd className="shrink-0" aria-hidden="true">
                        {cmd.shortcut}
                      </kbd>
                    )}
                    {cmd.hint && (
                      <span
                        className="shrink-0 text-[10px] uppercase tracking-wider"
                        style={{
                          color: isSelected ? "var(--accent)" : "var(--text-dim)",
                        }}
                      >
                        {cmd.hint}
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Footer hint row */}
          <div className="flex items-center justify-between px-3 py-1.5 border-t border-[color:var(--border)] text-[10px] uppercase tracking-wider text-[color:var(--text-dim)]">
            <span className="flex items-center gap-2">
              <kbd>↑</kbd>
              <kbd>↓</kbd>
              <span>navigate</span>
              <span className="opacity-50">·</span>
              <kbd>↵</kbd>
              <span>run</span>
            </span>
            <span className="tabular-nums">
              {filtered.length} / {commands.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
